// Every time shown or picked, in the person's time zone (see doc/feature/time-zone.md).
// The zone is `auth/me`'s `time_zone` (app-shell calls `setUserZone`), else the browser's.
// Times are shown on its clock and never with a zone name or offset.
// A "wall Date" is a Date whose UTC fields are the wall clock in the zone; calendar math runs on
// them, so the browser's own daylight saving gaps never move a time.

const pad = (n) => String(n).padStart(2, '0')
let chosen = ''

/** Whether `Intl` knows the zone. */
export function validZone(zone) {
  if (!zone || typeof zone !== 'string') return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone })
    return true
  } catch {
    return false
  }
}

/** The browser's IANA zone, or '' when it reports none Intl knows. */
export function browserZone() {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return validZone(zone) ? zone : ''
  } catch {
    return ''
  }
}

export function setUserZone(zone) {
  chosen = validZone(zone) ? zone : ''
}

/** The zone every time is shown in: the person's, else the browser's, else UTC. */
export function userZone() {
  return chosen || browserZone() || 'UTC'
}

const partFormats = new Map()

function partFormat(zone) {
  let format = partFormats.get(zone)
  if (!format) {
    format = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    partFormats.set(zone, format)
  }
  return format
}

function toInstant(value) {
  return value instanceof Date ? value : new Date(value)
}

function valid(date) {
  return !Number.isNaN(date.getTime())
}

/** `{ year, month, day, hour, minute, second }` of an instant on the zone's clock. */
export function wallParts(value, zone = userZone()) {
  const parts = {}
  for (const { type, value: part } of partFormat(zone).formatToParts(toInstant(value))) parts[type] = +part
  return { year: parts.year, month: parts.month, day: parts.day, hour: parts.hour % 24, minute: parts.minute, second: parts.second }
}

/** The wall Date of an instant: its UTC fields are the zone's clock. */
export function wallDate(value, zone = userZone()) {
  const at = toInstant(value)
  if (!valid(at)) return new Date(NaN)
  const p = wallParts(at, zone)
  return new Date(Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, at.getUTCMilliseconds()))
}

/** Minutes east of UTC of the zone at an instant (`-300` is UTC-05:00). */
export function offsetMinutes(zone = userZone(), at = new Date()) {
  const instant = toInstant(at)
  return Math.round((wallDate(instant, zone).getTime() - instant.getTime()) / 60000)
}

/** `UTC-06:00`, `UTC+05:45`. */
export function offsetText(minutes) {
  const abs = Math.abs(minutes)
  return `UTC${minutes < 0 ? '-' : '+'}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
}

/** `+02:00`, the ISO form of an offset. */
export function isoOffset(minutes) {
  return offsetText(minutes).slice(3)
}

/**
 * The instant of a wall time on the zone's clock: `YYYY-MM-DD`, `YYYY-MM-DDTHH:MM` or with
 * seconds (a datetime-local value). A time skipped by daylight saving lands an hour later.
 */
export function fromWall(text, zone = userZone()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(String(text || ''))
  if (!m) return new Date(NaN)
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0))
  const first = offsetMinutes(zone, new Date(guess))
  let at = guess - first * 60000
  const second = offsetMinutes(zone, new Date(at))
  if (second !== first) at = guess - second * 60000
  return new Date(at)
}

/** `YYYY-MM-DDTHH:MM` of an instant on the zone's clock, for a datetime-local field. */
export function wallInput(value, zone = userZone()) {
  const p = wallParts(value, zone)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`
}

/** `YYYY-MM-DD` of an instant on the zone's clock. */
export function dayOf(value, zone = userZone()) {
  const p = wallParts(value, zone)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

/** Today in the zone, `YYYY-MM-DD`. */
export function today() {
  return dayOf(new Date())
}

/** `(UTC-06:00) America/Chicago`, as the zone picker lists it. */
export function zoneLabel(zone, at = new Date()) {
  return `(${offsetText(offsetMinutes(zone, at))}) ${zone.replace(/_/g, ' ')}`
}

/** Every zone the browser knows (with UTC and `extra`), as `{ value, label, offset }` by offset, then name. */
export function zoneOptions(extra = '') {
  let zones = []
  try {
    zones = Intl.supportedValuesOf('timeZone')
  } catch {}
  zones = [...zones]
  if (!zones.includes('UTC')) zones.push('UTC')
  if (extra && validZone(extra) && !zones.includes(extra)) zones.push(extra)
  const now = new Date()
  return zones
    .map((zone) => ({ value: zone, offset: offsetMinutes(zone, now), label: zoneLabel(zone, now) }))
    .sort((a, b) => a.offset - b.offset || a.value.localeCompare(b.value))
}

/**
 * A zone for a browser that names none: the first one whose winter and summer offsets match this
 * device's clock; `UTC` when none does.
 */
export function guessZone(now = new Date()) {
  const year = now.getFullYear()
  const winter = new Date(year, 0, 1)
  const summer = new Date(year, 6, 1)
  const want = [-winter.getTimezoneOffset(), -summer.getTimezoneOffset()]
  const match = zoneOptions().find((option) => offsetMinutes(option.value, winter) === want[0] && offsetMinutes(option.value, summer) === want[1])
  return match ? match.value : 'UTC'
}

const formats = new Map()

function format(value, options, zone = userZone()) {
  const at = toInstant(value)
  if (!valid(at)) return ''
  const key = `${zone}|${JSON.stringify(options)}`
  let formatter = formats.get(key)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(undefined, { ...options, timeZone: zone })
    formats.set(key, formatter)
  }
  return formatter.format(at)
}

/** "Oct 21, 2026, 3:05 PM". */
export function fullDate(value) {
  return format(value, { dateStyle: 'medium', timeStyle: 'short' })
}

/** An instant with the given Intl date and time fields (no zone name), in the zone. */
export function dateTimeText(value, options = { dateStyle: 'medium', timeStyle: 'short' }) {
  return format(value, options)
}

/** An instant's day, "Oct 21, 2026" or the given Intl fields, in the zone. */
export function dateText(value, options = { dateStyle: 'medium' }) {
  return format(value, options)
}

/** "3:05 PM". */
export function timeText(value) {
  return format(value, { hour: 'numeric', minute: '2-digit' })
}

/** A list date: the time today, the day this year, else the full date. */
export function shortDate(value, now = new Date()) {
  const at = toInstant(value)
  if (!valid(at)) return ''
  const day = dayOf(at)
  if (day === dayOf(now)) return timeText(at)
  if (day.slice(0, 4) === dayOf(now).slice(0, 4)) return format(at, { month: 'short', day: 'numeric' })
  return format(at, { year: 'numeric', month: 'numeric', day: 'numeric' })
}

/** A calendar day `YYYY-MM-DD` as text: it is the same day everywhere. */
export function plainDay(day, options = { dateStyle: 'medium' }) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(day || ''))
  if (!m) return ''
  return format(new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])), options, 'UTC')
}

/** A wall Date (or a time of day on one) as text: its UTC fields are what is shown. */
export function wallText(date, options) {
  return format(date, options, 'UTC')
}
